import { useState } from 'react'

import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  ListType,
  REMOVE_LIST_COMMAND
} from '@lexical/list'
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode
} from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { $getNearestNodeOfType } from '@lexical/utils'
import { useMemoizedFn } from 'ahooks'
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  LexicalCommand
} from 'lexical'

import { $getSelectedElement } from '../../utils/element'
import { useEditor } from '../useEditor'
import { useSelectionChange } from '../useSelectionChange'

import { BlockType } from './interface'

const listCommandMap: Record<ListType, LexicalCommand<void>> = {
  check: INSERT_CHECK_LIST_COMMAND,
  bullet: INSERT_UNORDERED_LIST_COMMAND,
  number: INSERT_ORDERED_LIST_COMMAND
}

/**
 * 区块的类型
 */
export const useBlockType = () => {
  const editor = useEditor()

  const [blockType, _setBlockType] = useState<BlockType>('paragraph')

  const getBlockTypeHandler = () => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element = $getSelectedElement(anchorNode)
      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      if (elementDOM === null) {
        return false
      }
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode)
        const type = parentList
          ? parentList.getListType()
          : element.getListType()
        _setBlockType(type)
      } else {
        const type = $isHeadingNode(element) ? element.getTag() : 'paragraph'
        _setBlockType(type)
      }
    }
    return false
  }

  useSelectionChange(getBlockTypeHandler)

  const setBlockType = useMemoizedFn(
    ({
      type,
      resetIfSame = true
    }: {
      type: BlockType
      resetIfSame?: boolean
    }) => {
      if (
        type === 'paragraph' ||
        type === 'quote' ||
        type === 'h1' ||
        type === 'h2' ||
        type === 'h3' ||
        type === 'h4' ||
        type === 'h5' ||
        type === 'h6'
      ) {
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () =>
              type === 'paragraph'
                ? $createParagraphNode()
                : type === 'quote'
                  ? $createQuoteNode()
                  : $createHeadingNode(type)
            )
          }
        })
      } else if (type === 'check' || type === 'bullet' || type === 'number') {
        if (blockType !== type) {
          editor.dispatchCommand(listCommandMap[type], undefined)
        } else {
          if (resetIfSame) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
          }
        }
      }
    }
  )

  return [blockType, setBlockType] as const
}
